import { defineStore } from 'pinia'

import router from '@/router'

import { removeToken, setToken } from '@/api'
import * as authApi from '@/api/auth'

import { parseErrors } from '@/helpers/error-parsers'
import { LsGlobalKeys, lsRemove, lsSave } from '@/helpers/local-storage'
import { handleCommonModuleState } from '@/helpers/store-state'
import { showToast } from '@/helpers/messages'
import { hasEvAccess, hasGolfAccess, hasP2pAccess } from '@/helpers/rbac'

import { getInitialAuthState } from './state'

export const useAuthStore = defineStore({
  id: 'auth-store',
  state: getInitialAuthState,
  actions: {
    async test() {
      this.session?.scopes.push('update_trips')
    },

    async signIn(signInData: { email: string; password: string }) {
      try {
        this.$patch(state => {
          handleCommonModuleState('start', state.signInModule)
        })

        const session = (await authApi.signIn(signInData)).data.data

        // Fields are already optional, no need to explicitly set undefined
        // The fields will be undefined by default if they don't exist in the response

        this.session = session
        setToken(session.token)
        lsSave(LsGlobalKeys.AUTH, session)

        // Redirect statistics-viewer users directly to EV Statistics page
        const isStatisticsViewer =
          session.user.role === 'statistics-viewer' ||
          session.user.ev_role === 'statistics-viewer'

        // Redirect ev_car_owner users directly to My EV Cars Statistics page
        const isEvCarOwner = session.user.ev_role === 'ev_car_owner'

        const canP2p = hasP2pAccess(session.user)
        const canEv = hasEvAccess(session.user)
        const canGolf = hasGolfAccess(session.user)

        let initialModule: 'p2p' | 'ev' | 'golf' = 'p2p'
        if (canP2p) {
          initialModule = 'p2p'
        } else if (canGolf) {
          initialModule = 'golf'
        } else if (canEv) {
          initialModule = 'ev'
        }
        lsSave(LsGlobalKeys.CURRENT_MODULE, initialModule)

        if (isStatisticsViewer) {
          router.replace({ name: 'evStatistic' })
        } else if (isEvCarOwner) {
          router.replace({ name: 'evOwnerStatistics' })
        } else if (!canP2p && !canEv && canGolf) {
          router.replace({ name: 'golfDashboard' })
        } else if (!canP2p && canEv) {
          router.replace({ name: 'evVehicles' })
        } else {
          router.replace({ path: '/' })
        }

        this.$patch(state => {
          handleCommonModuleState('success', state.signInModule)
        })
      } catch (e) {
        console.error('🔍 signIn - error during login:', e)
        const { error, validationErrors: vErrors } = parseErrors(e)

        this.$patch(state => {
          handleCommonModuleState('error', state.signInModule, vErrors || error)
        })

        if (!vErrors) showToast({ type: 'error', message: error })
      }
    },

    async refreshSession() {
      try {
        const session = (
          await authApi.signIn({
            email: this.session?.user.email || '',
            password: '',
          })
        ).data.data

        // Fields are already optional, no need to explicitly set undefined
        // The fields will be undefined by default if they don't exist in the response

        this.session = session
        setToken(session.token)
        lsSave(LsGlobalKeys.AUTH, session)
      } catch (e) {
        console.error('Failed to refresh session:', e)
      }
    },
    async fetchMe() {
      if (!this.session) return
      try {
        const response = await authApi.getMe()
        const data = response.data.data
        if (data && data.user) {
          this.$patch(state => {
            if (state.session) {
              state.session.user = { ...state.session.user, ...data.user }
              if (data.scopes) {
                state.session.scopes = data.scopes
              }
            }
          })
          lsSave(LsGlobalKeys.AUTH, this.session)
        }
      } catch (e) {
        console.error('Failed to fetch user profile:', e)
      }
    },
    signOut() {
      this.$patch({ ...getInitialAuthState(), session: null })

      removeToken()
      lsRemove(LsGlobalKeys.AUTH)
      lsRemove(LsGlobalKeys.CURRENT_MODULE)
      lsRemove(LsGlobalKeys.LAST_ROUTE)

      router.replace({ path: '/login' })
    },
  },
  getters: {
    isAuthenticated(state) {
      const hasToken = Boolean(state.session?.token)
      const hasUser = Boolean(state.session?.user)

      return Boolean(hasToken && hasUser)
    },
    scopes(state) {
      return state.session?.scopes || []
    },
  },
})
